import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

/**
 * TitleFlow-styled confirmation modal replacing native window.confirm().
 * Handles its own busy/loading state to prevent double-clicks.
 * theme: 'dark' (IT portal) | 'light' (standard portals)
 */
export const ConfirmActionModal = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  tone = 'danger',
  theme = 'dark',
  onCancel,
  onConfirm,
}) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setBusy(false); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const dark = theme === 'dark';
  const confirmClasses = tone === 'danger'
    ? 'bg-red-600 hover:bg-red-500 text-white'
    : 'bg-blue-600 hover:bg-blue-500 text-white';

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      data-testid="confirm-action-modal"
    >
      <div
        className={`w-full max-w-[420px] rounded-2xl border shadow-2xl overflow-hidden ${
          dark ? 'bg-slate-900 border-slate-700/80 shadow-black/50' : 'bg-white border-slate-200 shadow-slate-900/20'
        }`}
        role="alertdialog"
        aria-modal="true"
      >
        <div className="px-5 pt-4 pb-3 flex items-start gap-3">
          <span className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
            tone === 'danger'
              ? (dark ? 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30' : 'bg-red-50 text-red-600 ring-1 ring-red-100')
              : (dark ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30' : 'bg-blue-50 text-blue-600 ring-1 ring-blue-100')
          }`}>
            <AlertTriangle className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
          </span>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className={`text-[15px] font-bold tracking-tight ${dark ? 'text-slate-100' : 'text-slate-800'}`} data-testid="confirm-modal-title">
              {title}
            </h3>
            <p className={`mt-1 text-[12.5px] leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="confirm-modal-message">
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="confirm-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={`px-5 py-3.5 flex justify-end gap-2 border-t ${dark ? 'border-slate-800 bg-slate-950/40' : 'border-slate-100 bg-slate-50/70'}`}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`h-8.5 px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-colors ${
              dark
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                : 'border-slate-200 text-slate-600 hover:bg-slate-100'
            } disabled:opacity-50`}
            data-testid="confirm-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-60 ${confirmClasses}`}
            data-testid="confirm-modal-confirm"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
